/**
 * @file This is a WIP Workflow Viewer V2
 */
import React from 'react';
import { Mutation, Query } from 'react-apollo';
// tslint:disable-next-line: no-var-requires no-require-imports
require('./reportValidityPolyfill');
import StartWorkflowQuery from './queries/StartWorkflow.graphql';
import RunWorkflowQuery from './queries/RunWorkflow.graphql';
import { errorMessage, fixInput, nonInputErrorMessages } from './Utilities/WorkflowInputUtilities';
import { getInputValue, renderInFragment } from './Utilities/WorkflowUIUtilities';
import { isDebitCardFormStep, renderDebitCardStep } from './DebitCardStep';
const isRadioInput = (props) => props.__typename === 'WorkflowRadioInput';
const isRadioCardWithInput = (props) => !!props && props.__typename === 'WorkflowInputValueRadioCardWithInput';
export class WorkflowUI extends React.Component {
    constructor() {
        super(...arguments);
        this.state = { inputState: {} };
        this._formRef = null;
        this._inputRef = React.createRef();
        this.stepCounter = 0;
        this.stepPropsForStep = (step) => ({
            formRef: r => { this._formRef = r; },
            inputRef: this._inputRef || null,
            actions: step.actions.map(action => {
                const skipValidation = !!action.opts.formnovalidate;
                return renderInFragment({
                    key: action.name,
                    node: this.props.renderAction({
                        action: {
                            ...action,
                            skipValidation
                        },
                        onClick: async () => this.runWorkflow(action.name, skipValidation),
                        submitted: this.props.submitted
                    })
                });
            }),
            inputs: step.inputs.map(input => renderInFragment({
                key: input.name,
                node: this.renderInput(input, step.errors)
            })),
            name: step.name,
            script: this.props.renderScript({
                script: step.script,
                legacyInformation: this.props.enableLegacyRenderScriptInfo
                    ? {
                        runWorkflow: this.legacyRunWorkflow,
                        checkValidity: () => !!this._formRef && this._formRef.reportValidity()
                    }
                    : undefined
            }),
            nonInputErrors: nonInputErrorMessages(step.errors),
            error: step.error,
            errors: step.errors,
            id: `step${this.stepCounter}`,
            submitted: this.props.submitted,
            onExit: step.onExit,
        });
        this.renderStep = (step) => renderInFragment({
            key: step.id,
            node: this.props.renderStep(this.stepPropsForStep(step))
        });
        this.renderInput = (input, errors) => {
            const innerInputs = this.getInnerInputs(input, errors);
            return this.props.renderInput({
                input: fixInput(input),
                updateInput: this.updateWorkflowInput(input.name),
                value: getInputValue(this.state.inputState[input.name], input.value),
                innerInputs: innerInputs || undefined,
                error: errorMessage(input.name, errors)
            });
        };
        // Radio inputs where values are Radio Cards are currently the only component that support inner inputs.
        // To enable workflow.js to manage to state of the inner input we must create the update functions for each value.
        this.getInnerInputs = (input, errors) => {
            if (!isRadioInput(input)) {
                return null;
            }
            return input.valuesAndDetails.reduce((acc, curr) => {
                if (isRadioCardWithInput(curr.details)) {
                    acc.set(curr.details.input.name, {
                        input: curr.details.input,
                        updateInput: this.updateWorkflowInput(curr.details.input.name),
                        value: curr.details.input.value || undefined,
                        error: errorMessage(input.name, errors)
                    });
                }
                return acc;
            }, new Map());
        };
        // Temporary, will remove when we implement the long term solution
        this.legacyRunWorkflow = async (action, inputs) => {
            if (!this.props.data) {
                throw new Error('Missing Previous State');
            }
            try {
                await this.props.runWorkflow({
                    variables: {
                        action,
                        inputs,
                        previousState: this.props.data.me.getWorkflow.state
                    }
                });
                this.stepCounter = this.stepCounter + 1;
                this.setState({ inputState: {} }, () => this._inputRef.current && this._inputRef.current.focus());
            }
            catch (e) {
                this.setState({ error: 'An error occurred' });
            }
        };
        this.runWorkflow = async (action, skipValidation = false) => {
            if (!this.props.data) {
                throw new Error('Missing Previous State');
            }
            if (!this._formRef || (!skipValidation && !this._formRef.reportValidity())) {
                return;
            }
            try {
                await this.props.runWorkflow({
                    variables: {
                        action,
                        inputs: Object.entries(this.state.inputState)
                            .map(([name, value]) => ({ name, value })),
                        previousState: this.props.data.me.getWorkflow.state
                    }
                });
                this.stepCounter = this.stepCounter + 1;
                this.setState({ inputState: {} }, () => this._inputRef.current && this._inputRef.current.focus());
            }
            catch (e) {
                this.setState({ error: 'An error occurred' });
            }
        };
        this.updateWorkflowInput = inputName => value => {
            this.setState(({ inputState }) => ({
                inputState: {
                    ...inputState,
                    [inputName]: value
                }
            }));
        };
    }
    componentDidUpdate() {
        const { data, loading, onStepDidMount } = this.props;
        if (!!data && !loading) {
            const { getWorkflow: workflow } = data.me;
            // tslint:disable-next-line no-non-null-assertion
            const step = [...workflow.steps].pop();
            if (step.name !== this.state.stepName) {
                this.setState({ stepName: step.name });
                if (onStepDidMount) {
                    onStepDidMount(this.stepPropsForStep(step));
                }
            }
        }
    }
    render() {
        const { children, data, loading, showAllSteps } = this.props;
        const baseProps = {
            loading,
            steps: []
        };
        if (loading || !data) {
            return children(baseProps);
        }
        const { getWorkflow: workflow } = data.me;
        const currentStep = [...workflow.steps].pop();
        if (!currentStep) {
            return children(baseProps);
        }
        let currentStepRender;
        if (isDebitCardFormStep({ workflowName: workflow.name, stepName: currentStep.name, script: currentStep.script })) {
            currentStepRender = renderDebitCardStep({
                uiprops: this.props,
                step: currentStep,
                counter: this.stepCounter,
                updateCounter: () => this.stepCounter + 1,
                inputRef: this._inputRef,
            });
        }
        else {
            currentStepRender = this.renderStep(currentStep);
        }
        if (showAllSteps) {
            const steps = workflow.steps.reduceRight((acc, curr) => acc.concat(this.renderStep(curr)), [currentStepRender]);
            return children({ ...baseProps, steps });
        }
        return children({ ...baseProps, steps: currentStepRender });
    }
}
const cacheUpdater = props => (cache, { data }) => {
    let queryResult = null;
    // readQuery was not safely returning null when the cache did not have the data expected
    // this check simply ensures that queryResult is null if it cannot be read from the cache
    try {
        queryResult = cache.readQuery({ query: StartWorkflowQuery, variables: props });
    }
    catch (error) {
        console.log(`workflow.js error on reading current cache value after mutation: JSON.stringify(error)`);
    }
    if (!queryResult || !data || !data.workflows || !data.workflows.runWorkflow || !data.workflows.runWorkflow.workflow) {
        return;
    }
    cache.writeQuery({
        query: StartWorkflowQuery,
        data: {
            ...queryResult,
            me: {
                ...queryResult.me,
                getWorkflow: {
                    ...queryResult.me.getWorkflow,
                    ...data.workflows.runWorkflow.workflow,
                    steps: [
                        ...queryResult.me.getWorkflow.steps,
                        data.workflows.runWorkflow.workflow.step
                    ]
                }
            }
        }
    });
};
export const WorkflowViewer = ({ variables, children, ...props }) => (React.createElement(Query, { query: StartWorkflowQuery, variables: variables, skip: props.finished, fetchPolicy: 'network-only' }, ({ data, loading }) => (React.createElement(Mutation, { mutation: RunWorkflowQuery, update: cacheUpdater(variables) }, (runWorkflow, { loading: submitted }) => (React.createElement(WorkflowUI, Object.assign({}, props, { children: children, data: data, loading: loading, submitted: submitted, runWorkflow: runWorkflow })))))));
